import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from "react";
import { ErrorWithRetry, LoadingMessage } from "../../components/UiStates";
import { Button } from "../../components/ui/Button";
import { Card, CardTitle } from "../../components/ui/Card";
import { useLanguage } from "../../lib/LanguageContext";
import { apiClient } from "../../services/api/client";
const LIVE_MODE = "live";
export const ShadowRolloutCard = () => {
    const { t } = useLanguage();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [runtime, setRuntime] = useState(null);
    const [enabled, setEnabled] = useState(false);
    const [canaryPercent, setCanaryPercent] = useState("0");
    const [strategyWhitelist, setStrategyWhitelist] = useState("");
    const [accountWhitelist, setAccountWhitelist] = useState("");
    const [savedMsg, setSavedMsg] = useState(false);
    const load = useCallback(() => {
        setLoading(true);
        setError("");
        setSavedMsg(false);
        void apiClient.policies.runtime.get(LIVE_MODE).then((result) => {
            if (!result.success) {
                setRuntime(null);
                setError(result.error.message);
                setLoading(false);
                return;
            }
            const cfg = result.data;
            setRuntime(cfg);
            setEnabled(Boolean(cfg.shadow_rollout?.enabled));
            setCanaryPercent(String(cfg.shadow_rollout?.canary_percent ?? 0));
            setStrategyWhitelist((cfg.shadow_rollout?.strategy_whitelist ?? []).join(","));
            setAccountWhitelist((cfg.shadow_rollout?.account_whitelist ?? []).join(","));
            setLoading(false);
        });
    }, []);
    useEffect(() => {
        load();
    }, [load]);
    const save = () => {
        setSaving(true);
        setError("");
        setSavedMsg(false);
        const canary = Number(canaryPercent);
        if (!Number.isFinite(canary) || canary < 0 || canary > 100) {
            setError(t("risk.validation"));
            setSaving(false);
            return;
        }
        const strategy_whitelist = strategyWhitelist
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        const account_whitelist = accountWhitelist
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        void apiClient.policies.runtime
            .update(LIVE_MODE, {
            shadow_rollout: {
                enabled,
                canary_percent: canary,
                strategy_whitelist,
                account_whitelist,
            },
        })
            .then((result) => {
            if (!result.success) {
                setError(result.error.message);
                setSaving(false);
                return;
            }
            setRuntime(result.data);
            setEnabled(Boolean(result.data.shadow_rollout?.enabled));
            setCanaryPercent(String(result.data.shadow_rollout?.canary_percent ?? 0));
            setStrategyWhitelist((result.data.shadow_rollout?.strategy_whitelist ?? []).join(","));
            setAccountWhitelist((result.data.shadow_rollout?.account_whitelist ?? []).join(","));
            setSaving(false);
            setSavedMsg(true);
        });
    };
    return (_jsxs(Card, { className: "mt-6", children: [_jsx(CardTitle, { className: "mb-2", children: t("settings.shadowRolloutTitle") }), _jsx("p", { className: "mb-4 text-sm text-muted-foreground", children: t("settings.shadowRolloutHint") }), _jsx("p", { className: "mb-4 rounded-md border border-border bg-muted/20 px-3 py-2 text-sm text-muted-foreground", children: t("settings.shadowRolloutScopeNote") }), loading ? _jsx(LoadingMessage, { text: t("risk.load") }) : null, error ? _jsx(ErrorWithRetry, { message: error, onRetry: load, disabled: saving, className: "mb-4" }) : null, !loading && !error && runtime ? (_jsxs(_Fragment, { children: [_jsxs("label", { className: "mb-4 flex items-center gap-2 text-sm text-foreground", children: [_jsx("input", { type: "checkbox", className: "h-4 w-4 rounded border-border", checked: enabled, onChange: (e) => setEnabled(e.target.checked), disabled: saving }), t("settings.shadowRolloutEnabled")] }), _jsxs("div", { className: "mb-4 grid gap-3 sm:grid-cols-2", children: [_jsxs("label", { className: "flex flex-col gap-2 text-sm", children: [_jsx("span", { className: "text-muted-foreground", children: t("settings.shadowRolloutCanary") }), _jsx("input", { className: "h-10 rounded-md border border-border bg-background px-3 text-foreground", inputMode: "numeric", value: canaryPercent, onChange: (e) => setCanaryPercent(e.target.value), disabled: saving }), _jsx("span", { className: "text-xs text-muted-foreground", children: t("settings.shadowRolloutCanaryHint") })] }), _jsxs("label", { className: "flex flex-col gap-2 text-sm", children: [_jsx("span", { className: "text-muted-foreground", children: t("settings.shadowRolloutStrategies") }), _jsx("input", { className: "h-10 rounded-md border border-border bg-background px-3 text-foreground", value: strategyWhitelist, onChange: (e) => setStrategyWhitelist(e.target.value), placeholder: "s-abc123,s-def456", disabled: saving }), _jsx("span", { className: "text-xs text-muted-foreground", children: t("settings.shadowRolloutStrategiesHint") })] }), _jsxs("label", { className: "flex flex-col gap-2 text-sm sm:col-span-2", children: [_jsx("span", { className: "text-muted-foreground", children: t("settings.shadowRolloutAccounts") }), _jsx("input", { className: "h-10 rounded-md border border-border bg-background px-3 text-foreground", value: accountWhitelist, onChange: (e) => setAccountWhitelist(e.target.value), placeholder: "u-reg-...", disabled: saving }), _jsx("span", { className: "text-xs text-muted-foreground", children: t("settings.shadowRolloutAccountsHint") })] })] }), _jsx("p", { className: "mb-4 text-xs text-muted-foreground", children: t("settings.shadowRolloutAlt") }), _jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [_jsx(Button, { type: "button", onClick: save, disabled: saving, children: saving ? t("common.loading") : t("settings.shadowRolloutSave") }), savedMsg ? _jsx("span", { className: "text-sm text-emerald-600 dark:text-emerald-400", children: t("settings.shadowRolloutSaved") }) : null] })] })) : null] }));
};
